import React, { useState, useEffect, useContext } from "react";
import { useTranslation } from "react-i18next";
import { useHistory, useParams } from "react-router-dom";
import PropTypes from "prop-types";

import {
  Grid,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  FormControlLabel,
  Checkbox,
  Select,
  MenuItem,
  makeStyles,
  useTheme,
} from "@material-ui/core";

import { RiderContext } from "../../../context";
import { FilledTextField } from "../../../components/FormElements";
import { useTestIds } from "../../../hooks/useTestIds";
import { withSnackbar } from "../../../HOC/withSnackbar";

const useStyles = makeStyles((theme) => ({
  formControl: {
    minWidth: 120,
    "& > *": {
      marginRight: theme.spacing(1),
    },
  },
}));

const ManageBonus = ({ setSnackbarMessage, setAlertSeverity }) => {
  const theme = useTheme();
  const classes = useStyles(theme);
  const history = useHistory();
  const testIds = useTestIds("rider-profile-manage-bonus-modal");
  const { t } = useTranslation();
  const { riderId } = useParams();
  const { alterBonus, wallet, fetchWallet, clearNegativeBalance } = useContext(RiderContext);
  const [isOpen, setIsOpen] = useState(true);
  const [amount, setAmount] = useState("");
  const [amountError, setAmountError] = useState(false);
  const [helperText, setHelperText] = useState(null);
  const [actionType, setActionType] = useState("add");
  const [clear, setClear] = useState(false);
  const [negativeBalance, setNegativeBalance] = useState(0);

  const clearWalletNegativeBalance = (event) => {
    setClear(event.target.checked);
  };

  useEffect(() => {
    if (!wallet) fetchWallet(riderId);
  }, [riderId, clear]);

  useEffect(() => {
    if (wallet && wallet?.balance?.real < 0) {
      setNegativeBalance(wallet.balance.real);
    } else setNegativeBalance(0);
  }, [wallet]);

  useEffect(() => {
    // TODO: do we have a standard form validation?
    const num = Number(amount);
    if (amount && Number.isNaN(num)) {
      setHelperText("Not a valid number");
      setAmountError(true);
    } else if (num && num <= 0) {
      setHelperText("Must be > 0.00");
      setAmountError(true);
    } else {
      setHelperText(null);
      setAmountError(false);
    }
  }, [amount]);

  const handleClose = (e = null) => {
    if (e) e.stopPropagation();
    setIsOpen(false);
    history.goBack();
  };

  const handleSubmit = async () => {
    if (amountError) return;
    if (amount) {
      const inputAmount = actionType === "add" ? Number(amount) : Number(amount) * -1;
      try {
        await alterBonus(inputAmount);
      } catch (error) {
        const errors = error.response.data;
        if (errors.bonus_amount) {
          setSnackbarMessage(errors.bonus_amount[0]);
        } else if (errors.non_field_errors) {
          setSnackbarMessage(errors.non_field_errors[0]);
        }
        setAlertSeverity("error");
      }
    } else if (!amount && !negativeBalance) return;
    if (clear && negativeBalance) {
      clearNegativeBalance(riderId);
    }
    handleClose();
    fetchWallet(riderId);
  };

  return (
    <Dialog
      data-testid={testIds.wrapper}
      open={isOpen}
      onClose={handleClose}
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle data-testid={testIds.title}>{t("Riders.Wallet.ManageWallet")}</DialogTitle>
      {!negativeBalance ? (
        <DialogContent data-testid={testIds.content}>
          <Grid container justify="space-between">
            <Grid item xs={12} md={4}>
              <FormControl variant="outlined" className={classes.formControl} margin="normal">
                <Select
                  data-testid={testIds.selectActionInput}
                  value={actionType}
                  onChange={(e) => setActionType(e.target.value)}
                >
                  <MenuItem data-testid={`${testIds.selectActionOption}-0`} value="add">
                    {t("Riders.Wallet.AddBonus")}
                  </MenuItem>
                  <MenuItem data-testid={`${testIds.selectActionOption}-1`} value="remove">
                    {t("Riders.Wallet.RemoveBonus")}
                  </MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={8}>
              <FilledTextField
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                label={t("Riders.Wallet.Amount")}
                error={amountError}
                helperText={helperText}
                mask={wallet?.currency}
                testIds={{
                  input: testIds.amountInput,
                }}
              />
            </Grid>
          </Grid>
        </DialogContent>
      ) : (
        <DialogContent>
          <Grid container justify="space-between">
            <Grid item xs={12}>
              <FormControl variant="outlined" className={classes.formControl} margin="normal">
                <FormControlLabel
                  control={
                    <Checkbox
                      data-testid={testIds.clearNegativeBalanceCheckbox}
                      checked={clear}
                      onChange={clearWalletNegativeBalance}
                    />
                  }
                  data-testid={testIds.clearNegativeBalanceCheckboxLabel}
                  label={t("Riders.Wallet.ClearNegativeBalance")}
                />
              </FormControl>
            </Grid>
          </Grid>
        </DialogContent>
      )}
      <DialogActions>
        <Button data-testid={testIds.cancelButton} onClick={handleClose}>
          {t("Riders.Wallet.Cancel")}
        </Button>
        <Button data-testid={testIds.saveButton} onClick={handleSubmit}>
          {t("Riders.Wallet.Save")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default withSnackbar(ManageBonus);
ManageBonus.propTypes = {
  setSnackbarMessage: PropTypes.func.isRequired,
  setAlertSeverity: PropTypes.func,
};

ManageBonus.defaultProps = {
  setAlertSeverity: () => {},
};
